<template>
  <div>
    <div v-if="!loaded" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader></f7-preloader>
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>
    <DxPopup
      :visible="showUploadFormPopup"
      :drag-enabled="false"
      :close-on-outside-click="false"
      :show-close-button="true"
      :show-title="true"
      width="500"
      height="auto"
      container=".dx-viewport"
      :title="
        editData.isEditing
          ? $t('editFile') + ' - ' + formatRoute
          : $t('uploadFile') + ' - ' + formatRoute
      "
      :wrapper-attr="{ class: 'upload-popup' }"
      @hiding="hide"
    >
      <form id="form" ref="formUpload" @submit="onSubmit">
        <div class="dx-fieldset">
          <div class="dx-field">
            <div class="dx-field-label">
              {{ $t('title') }}: <i style="color: #a80d0b">*</i>
            </div>
            <DxTextBox
              :value="editData.isEditing ? editData.data.name : name"
              name="title"
              :placeholder="$t('title')"
              class="dx-field-value"
              :on-value-changed="onNameChanged"
            >
              <DxValidator>
                <DxRequiredRule :message="$t('requiredTitle')" />
              </DxValidator>
            </DxTextBox>
          </div>
          <div class="dx-field">
            <div class="dx-field-label">{{ $t('contractNum') }}:</div>
            <DxTextBox
              name="contractNumber"
              :placeholder="$t('contractNum')"
              :value="
                editData.isEditing
                  ? editData.data.contractNumber
                  : contractNumber
              "
              class="dx-field-value"
              :on-value-changed="onContractNumChanged"
            />
          </div>
          <div class="dx-field">
            <div class="dx-field-label">
              {{ $t('agronomySample.summary.description') }}:
            </div>
            <DxTextArea
              name="description"
              :placeholder="$t('agronomySample.summary.description')"
              :value="
                editData.isEditing
                  ? editData.data.description
                  : description
              "
              class="dx-field-value"
              :on-value-changed="onDescriptionChanged"
            />
          </div>
        </div>

        <div v-if="!editData.isEditing">
          <p class="item-title-bold marginLeft25">
            {{ $t('notification') }}
          </p>

          <div class="dx-field">
            <div class="dx-field-label">
              <span class="marginLeft25">{{ $t('emails') }}</span>
            </div>
            <div class="dx-field-value">
              <DxTagBox
                :accept-custom-value="true"
                name="emails"
                :placeholder="$t('emails')"
                :width="400"
                :value="
                  editData.isEditing ? editData.data.emails : emails
                "
                :on-value-changed="emailsChanged"
                @customItemCreating="onCustomEmailCreating"
              />
            </div>
          </div>
        </div>

        <div
          v-if="!editData.isEditing"
          class="fileuploader-container marginLeft15"
        >
          <DxFileUploader
            :select-button-text="$t('uploadFile')"
            label-text=""
            :max-file-size="5000000"
            upload-mode="useForm"
            @valueChanged="checkSize"
          >
            <DxValidator>
              <DxRequiredRule :message="$t('requiredUpload')" />
            </DxValidator>
          </DxFileUploader>
          <em class="marginLeft10">
            Max
            <strong style="color: #a80d0b">
              {{ sizeLimit }} {{ sizeLimitUnit }}
            </strong>
          </em>

          <p
            v-if="exceedsSizeLimit"
            class="max-size-label marginLeft10"
            style="color: #a80d0b"
          >
            {{ $t('fileExceedsSize') }}
          </p>
        </div>

        <f7-row>
          <f7-col width="100" style="text-align: left">
            <div class="dx-fieldset">
              <DxButton
                class="standardBtn"
                :use-submit-behavior="true"
                :text="$t('save')"
                type="outlined"
              />
            </div>
          </f7-col>
        </f7-row>
      </form>
    </DxPopup>
  </div>
</template>

<script>
import { DxPopup } from 'devextreme-vue/popup';
import { mapActions, mapState } from 'vuex';

import { DxFileUploader } from 'devextreme-vue/file-uploader';
import { DxTextBox } from 'devextreme-vue/text-box';

import DxButton from 'devextreme-vue/button';

import { DxTextArea } from 'devextreme-vue/text-area';

import DxTagBox from 'devextreme-vue/tag-box';
import EventBus from '@/services/event-bus';
import notify from 'devextreme/ui/notify';

import {
  DxValidator,
  DxRequiredRule,
} from 'devextreme-vue/validator';
import { NOTIFY_TIME_LONG } from '@/js/constants';

export default {
  components: {
    DxPopup,
    DxFileUploader,
    DxTextBox,
    DxValidator,
    DxRequiredRule,
    DxButton,
    DxTextArea,
    DxTagBox,
  },
  props: {
    editData: {
      type: Object,
      default: () => {
        return {
          isEditing: false,
          data: null,
        };
      },
    },
    address: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      name: '',
      contractNumber: '',
      description: '',
      emails: [],
      sizeLimit: 5,
      sizeLimitUnit: 'MB',
      exceedsSizeLimit: false,
      loaded: false,
    };
  },
  computed: {
    ...mapState('breadcrumb', ['items']),
    ...mapState('documentation', ['showUploadFormPopup', 'atc']),
    formatRoute() {
      return this.items[0].name;
    },
  },
  beforeMount() {
    if (this.editData.isEditing) {
      this.name = this.editData.data.name;
      this.emails = this.editData.data.emails;
      this.contractNumber = this.editData.data.contractNumber;
      this.description = this.editData.data.description;
    }
  },
  methods: {
    hide() {
      this.setUploadFormPopup(false);
    },
    onCustomEmailCreating() {
      // TODO: Definir como se quieren los correos de este campo
    },
    checkSize(e) {
      const kb = e.value[0].size / 1024;
      const mb = (kb / 1024).toFixed(2);

      if (Number(mb) > this.sizeLimit) {
        this.exceedsSizeLimit = true;
      } else {
        this.exceedsSizeLimit = false;
      }
    },
    async onSubmit(e) {
      e.preventDefault();

      if (this.exceedsSizeLimit) return;

      if (this.editData.isEditing) {
        this.onSubmitEdit(e);
        return;
      }

      const file = this.$refs.formUpload[5].files[0];

      try {
        const reader = new FileReader();
        reader.readAsDataURL(file);

        const extension = this.getFileExtension(file);

        reader.onload = async () => {
          notify(
            {
              message: this.$t('uploading'),
              width: 450,
            },
            'info',
            2000
          );
          this.loaded = false;
          await this.uploadFile({
            name: this.name,
            fileB64: reader.result,
            atc: this.atc,
            extension,
            fileSize: file.size,
            description: this.description,
            contractNumber: this.contractNumber,
            emails: this.emails,
            address: this.address,
          });

          const fs = await this.getTree(this.atc);

          EventBus.$emit('fileUploaded', fs);

          this.setUploadFormPopup(false);
          notify(
            {
              closeOnClick: true,
              message: this.$t('uploadSuccess'),
              width: 450,
            },
            'success',
            2000
          );

          this.loaded = true;
        };

        reader.onerror = (error) => {
          // TODO: ERROR. TRATAR CON HELPERS
          console.error('Error: ', error);
        };
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                JSON.parse(error.message).error
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.loaded = true;
      }
    },
    async onSubmitEdit(e) {
      try {
        notify(
          {
            message: this.$t('editing'),
            width: 450,
          },
          'info',
          2000
        );
        this.loaded = false;
        await this.editFile({
          id: this.editData.data.id,
          name: this.name,
          description: this.description,
          contractNumber: this.contractNumber,
          emails: this.emails,
          address: this.address,
        });

        const fs = await this.getTree(this.atc);

        EventBus.$emit('fileUploaded', fs);

        this.setUploadFormPopup(false);
        notify(
          {
            closeOnClick: true,
            message: this.$t('editSuccess'),
            width: 450,
          },
          'success',
          2000
        );
        this.loaded = true;
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                JSON.parse(error.message).error
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      }
    },
    getFileExtension(file) {
      const lastIndexDot = file.name.lastIndexOf('.');
      const splitted = file.name.split(file.name[lastIndexDot]);

      return splitted[splitted.length - 1];
    },
    emailsChanged(e) {
      this.emails = e.value;
    },
    onNameChanged(e) {
      this.name = e.value;
    },
    onContractNumChanged(e) {
      this.contractNumber = e.value;
    },
    onDescriptionChanged(e) {
      this.description = e.value;
    },
    ...mapActions('documentation', [
      'setUploadFormPopup',
      'uploadFile',
      'editFile',
      'getTree',
    ]),
  },
};
</script>
<style>
/* ===========================
   POPUP ESTILO FIGMA
   SOLO PARA ESTE POPUP
=========================== */

.upload-popup .dx-popup-content {
  padding: 0 24px 24px 24px !important;
}

/* Título del popup, sin franja azul y alineado a 24px */
.upload-popup .dx-popup-title {
  background: #fff !important;
  border-bottom: none !important;
  padding: 24px 24px 0 24px !important;
  margin-bottom: 8px !important; /* poco espacio con el primer label */
}

/* Icono de cerrar */
.upload-popup .dx-popup-title .dx-closebutton .dx-icon {
  font-size: 22px;
}

/* Quitamos márgenes del fieldset que te estaban empujando todo a la derecha */
.upload-popup .dx-popup-content .dx-fieldset {
  margin: 0 !important;
  padding: 0 !important;
}

/* Cada campo en columna */
.upload-popup .dx-field {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}

/* Labels */
.upload-popup .dx-field-label {
  font-size: 14px;
  font-weight: 600;
  color: #00346a;
}

/* Inputs ocupan todo el ancho útil */
.upload-popup .dx-field-value,
.upload-popup .dx-textarea,
.upload-popup .dx-tagbox {
  width: 100% !important;
}

/* Dentro del popup, anulamos las clases de margen que desalinean */
.upload-popup .marginLeft25,
.upload-popup .marginLeft15 {
  margin-left: 0 !important;
}

/* FileUploader alineado con el resto (24px ya viene del padding del popup) */
.upload-popup .fileuploader-container {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
}

/* Texto Max 5MB */
.upload-popup .fileuploader-container em {
  margin-top: 6px;
  font-size: 13px;
}

/* Botón Guardar centrado */
.upload-popup .standardBtn {
  margin-top: 10px;
}
/* ======================================
   INPUTS ESTILO FIGMA PARA ESTE POPUP
   (TextBox, TextArea, TagBox, Selects...)
====================================== */

.upload-popup .dx-texteditor-input,
.upload-popup .dx-texteditor-input-container {
  height: 40px !important;
  padding: 12px !important;
  font-size: 14px;
  align-items: center;
}

/* CONTENEDOR DEL INPUT */
.upload-popup .dx-texteditor {
  border-radius: 10px !important;
  border: 1px solid #00346a !important; /* borde azul suave */
  background: #fff !important;
  box-sizing: border-box;
  height: 40px !important;
  display: flex !important;
  align-items: center !important;
}

/* TEXTAREA igual estilo pero más alto */
.upload-popup .dx-textarea .dx-texteditor-input {
  height: auto !important;
  min-height: 80px !important;
  padding: 12px !important;
}

.upload-popup .dx-textarea {
  border-radius: 10px !important;
  border: 1px solid #00346a !important;
}

/* HOVER */
.upload-popup .dx-texteditor:hover {
  border-color: #00346a !important;
}

/* FOCUS */
.upload-popup .dx-texteditor.dx-state-focused {
  border-color: #00346e !important;
  box-shadow: 0 0 0 2px rgba(0, 52, 110, 0.15) !important;
}

/* PLACEHOLDER estilo Figma */
.upload-popup .dx-placeholder {
  font-size: 14px !important;
  opacity: 0.6 !important;
}

/* TAGBOX – inputs tags estilo igual */
.upload-popup .dx-tagbox {
  border-radius: 10px !important;
  border: 1px solid #00346a !important;
  padding-left: 8px !important;
}

.upload-popup .dx-tag {
  background-color: rgba(0, 52, 110, 0.1) !important;
  border-radius: 6px !important;
}

/* SELECTBOX arrow alineado */
.upload-popup .dx-dropdowneditor-icon {
  right: 10px !important;
}

/* DateBox igual a inputs */
.upload-popup .dx-datebox {
  border-radius: 10px !important;
  border: 1px solid #00346a !important;
}
.dx-fileuploader-wrapper {
  padding-left: 0px;
  margin-left: 0;
}
.upload-popup .dx-fileuploader .dx-button-content .dx-icon {
  display: none !important;
}
.upload-popup .dx-fileuploader .dx-button-content::before {
  content: '';
  display: inline-block;
  width: 26px;
  height: 26px;
  margin-right: 8px; /* separación con el texto */
  background-image: url(data:image/svg+xml;base64,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);
  background-repeat: no-repeat;
  background-size: contain;
}
.dx-popup-title.dx-toolbar .dx-toolbar-label {
  color: var(--Primary-Blue-60, #04509f);
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
}

.dx-fileuploader .dx-button-content {
  font-weight: 600;
  border: 0px solid var(--Primary-Blue-70, #00346a);
}
/* Igualamos la altura del botón del FileUploader al de Guardar */
.upload-popup .dx-fileuploader .dx-button {
  height: 40px !important;
  border-radius: 10px !important;
  display: flex !important;
  align-items: center !important;
}

/* Ajuste del contenido del botón (icono + texto) */
.upload-popup .dx-fileuploader .dx-button-content {
  height: 40px !important;
  line-height: 40px !important;
  display: flex !important;
  align-items: center !important;
  font-size: 14px !important;
  font-weight: 600 !important;
}
.item-title-bold {
  font-weight: 600;
  font-size: 16px;
  color: var(--Primary-Blue-60, #04509f);
}
/* Aplicamos el mismo estilo de foco a los botones del FileUploader */
.upload-popup .dx-fileuploader .dx-button.dx-state-focused,
.upload-popup
  .dx-fileuploader
  .dx-button.dx-button-mode-contained.dx-state-active {
  border-color: #00346e !important; /* Color del borde al enfocar */
  box-shadow: 0 0 0 2px rgba(0, 52, 110, 0.15) !important; /* El anillo azul de 2px */
}
.upload-popup .fileuploader-container em {
  margin-top: -10px;
  margin-bottom: 20px;
}
</style>
